import Game from "../../../core/engine/game";
import GameView from "../../../core/engine/views/game-view";
import { GameController } from "../../controller/GameController";
import RemoteGameManager from "../managers/RemoteGameManager";
import LocalGameManager from "../managers/LocalGameManager";
import { IInputSource } from "../../managers/input-source";
import { BoardState, PlayerState } from "../../../core/protocol/types";
import clickSound from "../../../core/engine/sounds/click_3.wav";
import startSound from "../../../core/engine/sounds/zingle.wav";

export interface GameLoopCallbacks {
  onStatsUpdate?: (stats: { score: number; level: number; lines: number }) => void;
  onCountdownChange?: (countdown: number) => void;
  onCountdownFinished?: () => void;
  onGameInitialized?: (game: Game, view: GameView) => void;
}

/**
 * MultiplayerGameLoop orchestrates the game loop for multiplayer games.
 * This class is decoupled from React and runs independently of React lifecycle.
 * 
 * Responsibilities:
 * - Orchestrates game loop (100fps stepping)
 * - Coordinates LocalGameManager and RemoteGameManager
 * - Packet processing coordination (P_SERVERSTATE)
 * - Countdown management and audio
 * - Stats collection and board state updates
 * - Lifecycle management (start/stop)
 * - Rendering
 * 
 * Note: UI/DOM concerns (keyboard listeners, resize handling) are handled by React component.
 */
export class MultiplayerGameLoop {
  private localGameManager: LocalGameManager | null = null;
  private remoteGameManager: RemoteGameManager | null = null;
  private view: GameView | null = null;
  private controller: GameController | null = null;
  private callbacks: GameLoopCallbacks = {};
  private rafId: number | null = null;
  private loopTimerId: ReturnType<typeof setTimeout> | null = null;
  private isRunning: boolean = false;
  private gameAccumulator: number = 0;
  private lastGameStepTime: number = 0;
  private lastUpdateTime: number = 0;
  private readonly loopTickMs: number = 1000 / 100;
  private updateInterval: number = 1000 / 60;
  private lastCountdownNumber: number = -1;
  private countdownAudio: HTMLAudioElement | null = null;
  private startAudio: HTMLAudioElement | null = null;
  private gameInitialized: boolean = false;
  private currentStatus: string | undefined = undefined;
  private currentCountdown: number | undefined = undefined;
  private lastServerCountdown: number | undefined = undefined;
  private currentGameMode: any = null;

  /**
   * Initialize the game loop with canvas and dependencies
   */
  initialize(
    canvas: HTMLCanvasElement,
    canvasSize: number,
    gameSeed: number | undefined,
    controller: GameController,
    localGameManager: LocalGameManager,
    remoteGameManager: RemoteGameManager,
    inputSources: IInputSource[],
    callbacks: GameLoopCallbacks
  ): void {
    if (this.gameInitialized) {
      return;
    }

    this.controller = controller;
    this.localGameManager = localGameManager;
    this.remoteGameManager = remoteGameManager;
    this.callbacks = callbacks;

    // Create local game via LocalGameManager
    const game = localGameManager.createOrUpdateLocalGame(
      canvas,
      gameSeed,
      controller,
      inputSources
    );

    // Create view
    this.view = new GameView(canvas, game, canvasSize);

    this.gameInitialized = true;

    // Notify callback
    if (this.callbacks.onGameInitialized) {
      this.callbacks.onGameInitialized(game, this.view);
    }
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.isRunning || !this.localGameManager || !this.view) {
      return;
    }

    const game = this.localGameManager.getLocalGame();
    if (!game) {
      return;
    }

    this.isRunning = true;
    this.lastGameStepTime = performance.now();
    this.lastUpdateTime = 0;
    this.gameAccumulator = 0;

    const runLoop = (frameTime: number) => {
      if (!this.isRunning || !this.localGameManager || !this.view) {
        return;
      }

      const localGame = this.localGameManager.getLocalGame();
      if (!localGame) {
        return;
      }

      const elapsed = frameTime - this.lastGameStepTime;
      this.lastGameStepTime = frameTime;
      this.gameAccumulator += elapsed;

      // Cap accumulator to prevent excessive catch-up (matches original quadra.cc:497-500)
      if (this.gameAccumulator > 300) {
        const overmind = localGame.overmind;
        if (overmind && (overmind as any).framecount !== undefined) {
          (overmind as any).framecount += Math.floor((this.gameAccumulator - 300) / 10);
        }
        this.gameAccumulator = 300;
      }

      // Game logic at 100fps (every 10ms)
      while (this.gameAccumulator >= 10) {
        this.gameAccumulator -= 10;
        this.stepGame();
      }

      // Update input AFTER game logic (matches original quadra.cc:449)
      this.localGameManager.updateInput();

      // Render
      this.view.draw(frameTime);

      // Update stats and board state at 60fps
      const deltaTime = frameTime - this.lastUpdateTime;
      if (deltaTime >= this.updateInterval) {
        this.updateStatsAndBoard();
        this.lastUpdateTime = frameTime;
      }

      this.scheduleNextFrame(runLoop);
    };

    runLoop(performance.now());
  }

  /**
   * Stop the game loop and cleanup
   */
  stop(): void {
    this.isRunning = false;

    this.cancelScheduledFrame();

    if (this.localGameManager) {
      this.localGameManager.cleanup();
    }
  }

  /**
   * Update game status and handle lifecycle changes
   */
  updateStatus(
    status: string | undefined,
    countdown: number | undefined,
    gameMode: any,
    isPaused?: boolean
  ): void {
    if (!this.localGameManager) return;

    const game = this.localGameManager.getLocalGame();
    if (!game) return;

    this.currentStatus = status;
    this.currentCountdown = countdown;
    this.currentGameMode = gameMode;

    if (typeof countdown === "number") {
      game.delayStart = countdown;
    } else {
      game.delayStart = 0;
    }

    if (typeof isPaused === "boolean") {
      if (game.paused !== isPaused) {
        console.log(
          `[MultiplayerGameLoop] Setting game.paused=${isPaused} (status=${status}, countdown=${countdown})`
        );
      }
      game.paused = isPaused;
      if (this.remoteGameManager) {
        this.remoteGameManager.setPaused(isPaused);
      }
    }

    this.handleCountdownSync(countdown);

    if (status === "active") {
      // Safety check: ensure countdown has finished before starting
      if (countdown !== undefined && countdown !== 0 && countdown !== 500) {
        return;
      }

      // Only start if game hasn't been started yet
      if (!game.timeout && !game.multiplayerStarted && !game.mainExecutor?.getTopState()) {
        console.log(`[MultiplayerGameLoop] Setting multiplayerStarted=true, status=${status}`);
        gameMode.handleGameStart?.(game);
        game.start();
        game.multiplayerStarted = true;
      }
    }

    if (status === "waiting" || status === "starting") {
      if (game.timeout) {
        game.stop();
      }
      game.multiplayerStarted = false;
    }
  }

  /**
   * Get the game instance
   */
  getGame(): Game | null {
    return this.localGameManager?.getLocalGame() || null;
  }

  /**
   * Get the view instance
   */
  getView(): GameView | null {
    return this.view;
  }

  /**
   * Resize the view (called from React component)
   */
  resizeView(newSize: number): void {
    if (this.view) {
      this.view.resize(newSize);
    }
  }

  /**
   * Step game logic (100fps)
   */
  private stepGame(): void {
    if (!this.localGameManager || !this.controller) return;

    // Step local game
    this.localGameManager.step();

    // Step remote games
    if (this.remoteGameManager) {
      this.remoteGameManager.processLineAttacks();
      this.remoteGameManager.stepAll();
    }

    // Process P_SERVERRANDOM packet BEFORE syncpoint packets (matches original net_list loop)
    // Reference: quadra/source/net_list.cc:343-371 (seed processed before syncpoint transitions)
    this.localGameManager.processServerRandomPacket();

    // Process queued P_SERVERSTATE packets
    this.controller.processQueuedServerStatePackets();

    // Process P_ENDGAME packet (matches original check_end_game() client side)
    // Reference: quadra/source/net_list.cc:746-761
    // Original: check_end_game() is called after processing P_LINES and canvas step
    // Original peekpacket() only checks front of queue, so P_ENDGAME must be at front
    if (this.localGameManager) {
      this.localGameManager.processEndgamePacket();
    }

  }

  /**
   * Play countdown audio
   */
  private playCountdownAudio(countdown: number): void {
    if (!countdown || countdown === 0 || countdown === 500) {
      this.lastCountdownNumber = -1;
      return;
    }

    const countdownNumber = Math.floor(countdown / 100) + 1;

    if (countdown === 1) {
      // Play start sound
      if (!this.startAudio) {
        this.startAudio = new Audio(startSound);
        this.startAudio.volume = 0.15;
      }
      this.startAudio.currentTime = 0;
      this.startAudio.play().catch((err) => console.warn("Could not play start sound:", err));
      this.lastCountdownNumber = countdownNumber;
      return;
    }

    if (countdownNumber !== this.lastCountdownNumber && countdownNumber > 0) {
      // Play countdown sound
      if (!this.countdownAudio) {
        this.countdownAudio = new Audio(clickSound);
        this.countdownAudio.volume = 0.15;
      }
      this.countdownAudio.currentTime = 0;
      this.countdownAudio.playbackRate = 1.2;
      this.countdownAudio.play().catch((err) => console.warn("Could not play countdown sound:", err));
      this.lastCountdownNumber = countdownNumber;
    }
  }

  /**
   * Sync countdown-related callbacks and audio with server-provided value
   */
  private handleCountdownSync(countdown: number | undefined): void {
    if (countdown === undefined) {
      this.lastCountdownNumber = -1;
      this.lastServerCountdown = undefined;
      return;
    }

    if (
      this.callbacks.onCountdownChange &&
      countdown !== this.lastServerCountdown
    ) {
      this.callbacks.onCountdownChange(countdown);
    }

    if (
      countdown === 0 &&
      this.lastServerCountdown !== undefined &&
      this.lastServerCountdown > 0 &&
      this.callbacks.onCountdownFinished
    ) {
      this.callbacks.onCountdownFinished();
    }

    if (countdown && countdown !== 500) {
      this.playCountdownAudio(countdown);
    } else {
      this.lastCountdownNumber = -1;
    }

    this.lastServerCountdown = countdown;
  }

  /**
   * Update stats and board state (60fps)
   */
  private updateStatsAndBoard(): void {
    if (!this.localGameManager || !this.controller) return;

    if (this.currentStatus !== "active") {
      return;
    }

    const game = this.localGameManager.getLocalGame();
    if (!game) return;

    const stats = {
      score: game.stats["SCORE"].getValue(),
      level: game.canvas.level,
      lines: game.stats["LINESTOT"].getValue(),
    };

    // Notify stats update
    if (this.callbacks.onStatsUpdate) {
      this.callbacks.onStatsUpdate(stats);
    }

    // Build board state
    const grid: number[][] = [];
    for (let row = 12; row < 32; row++) {
      const gridRow: number[] = [];
      for (let col = 4; col < 14; col++) {
        if (game.canvas.occupied[row] && game.canvas.occupied[row][col]) {
          gridRow.push(game.canvas.blocks[row][col] || 0);
        } else {
          gridRow.push(0);
        }
      }
      grid.push(gridRow);
    }

    const currentPiece = game.canvas.bloc
      ? {
          type: game.canvas.bloc.quel,
          rotation: game.canvas.bloc.rotation,
          x: game.canvas.bloc.x,
          y: game.canvas.bloc.y,
          color: game.canvas.bloc.color,
          posX: game.canvas.bloc.posX,
          posY: game.canvas.bloc.posY,
        }
      : undefined;

    const boardState: BoardState = {
      grid: grid,
      width: 10,
      height: 20,
      currentPiece: currentPiece,
      clearingLines: [] as number[],
      pendingGarbage: [] as Array<{ lines: number; holes: number[] }>,
    };

    const currentGameState = this.controller.getGameState();
    const isDead =
      (game.canvas.idle === 2 || game.canvas.dying) &&
      currentGameState?.status === "active";

    if (!isDead) {
      const ppm = game.stats["PPM"]?.getValue() || 0;
      const bpm = game.stats["BPM"]?.getValue() || 0;

      const playerState: Partial<PlayerState> = {
        score: stats.score,
        level: stats.level,
        lines: stats.lines,
        board: boardState,
        isActive: true,
        state: "normal",
        ppm: ppm,
        bpm: bpm,
      };

      this.controller.updatePlayerState(playerState);
    }
  }

  private scheduleNextFrame(callback: (frameTime: number) => void): void {
    this.cancelScheduledFrame();
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      this.rafId = window.requestAnimationFrame(callback);
    } else {
      this.loopTimerId = setTimeout(() => callback(performance.now()), this.loopTickMs);
    }
  }

  private cancelScheduledFrame(): void {
    if (this.rafId !== null && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.loopTimerId !== null) {
      clearTimeout(this.loopTimerId);
      this.loopTimerId = null;
    }
  }
}

